import { useEffect, useState, type ReactNode, type RefObject } from "react";
import { ArrowUpRight, Check, LoaderCircle } from "lucide-react";
import type { Snapshot } from "./api";
import { PageHeader } from "./ui/PageHeader";
import { EmptyState } from "./ui/EmptyState";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import "./ui/ui.css";
// The design system (docs/DESIGN-SYSTEM.md): the shell's most used pieces are re-exported here.
export { Badge } from "./ui/Badge";
export { Modal } from "./ui/Dialog";
export { DataState, Pager, QueryView, Skeleton } from "./ui/DataState";
export type PageProps = {
  s: Snapshot;
  reload: () => Promise<void>;
  go: (page: string) => void;
  notify: (message: string, error?: boolean) => void;
  modal: (node: ReactNode) => void;
  close: () => void;
  mutate: (path: string, body?: any, method?: string) => Promise<any>;
  /** Signs out (warns first when count entries have not been uploaded yet). */
  signOut?: () => void;
  /** Switches this login to another company the person belongs to (a fresh session). */
  switchCompany?: (companyId: string) => Promise<void>;
};
/** The standard empty state (ui/EmptyState.tsx); kept for older call sites. */
export const Empty = ({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) => (
  <EmptyState title={title} action={action}>
    {children}
  </EmptyState>
);
/** The standard page header (ui/PageHeader.tsx); kept for older call sites. */
export const PageHead = ({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children?: ReactNode;
}) => <PageHeader eyebrow={eyebrow} title={title} description={description} actions={children} />;
export function Form({
  children,
  onSubmit,
  submit = "Save",
  onCancel,
}: {
  children: ReactNode;
  onSubmit: (data: FormData) => Promise<any>;
  submit?: string;
  onCancel?: () => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        const data = new FormData(e.currentTarget);
        try {
          await onSubmit(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : "That did not save. Check your connection and try again.");
        } finally {
          setBusy(false);
        }
      }}
    >
      {children}
      {error && (
        <div role="alert" className="error-box">
          {error}
        </div>
      )}
      <footer className="form-footer">
        {onCancel && (
          <button type="button" className="button secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button className="button" disabled={busy} type="submit">
          {busy ? (
            <LoaderCircle className="spin" size={17} />
          ) : (
            <Check size={17} />
          )}{" "}
          {busy ? "Saving…" : submit}
        </button>
      </footer>
    </form>
  );
}
export function Field({
  label,
  name,
  value,
  type = "text",
  required = false,
  hint,
  min,
  step,
  placeholder,
  children,
}: {
  label: string;
  name: string;
  value?: any;
  type?: string;
  required?: boolean;
  hint?: string;
  min?: number;
  step?: string;
  placeholder?: string;
  children?: ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {required && <b> *</b>}
      </span>
      {children || (
        <input
          name={name}
          defaultValue={value ?? ""}
          type={type}
          required={required}
          min={min}
          step={step}
          placeholder={placeholder}
        />
      )}{" "}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export const Table = ({
  headers,
  children,
}: {
  headers: string[];
  children: ReactNode;
}) => (
  <div className="table-scroll">
    <table>
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
export const LinkButton = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => (
  <button className="text-button" onClick={onClick}>
    {children}
    <ArrowUpRight size={15} />
  </button>
);
export const Categories: Record<string, string> = {
  spirits: "Spirits",
  wine: "Wine",
  beer: "Beer & RTDs",
  keg: "Kegs",
  batch: "Batches",
  other: "Other",
};
export const getString = (d: FormData, k: string) =>
  String(d.get(k) || "").trim();
export const getNumber = (d: FormData, k: string, fallback = 0) => {
  const v = getString(d, k);
  return v === "" ? fallback : Number(v);
};
export const optionalNumber = (d: FormData, k: string) =>
  getString(d, k) === "" ? null : Number(d.get(k));
/** A yes/no question before a change (ui/ConfirmDialog.tsx has reasons, danger tone and type-to-confirm). */
export function Confirm({
  title,
  description,
  label = "Confirm",
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  label?: string;
  onConfirm: () => Promise<any>;
  onClose: () => void;
}) {
  return <ConfirmDialog title={title} description={description} confirmLabel={label} onConfirm={() => onConfirm()} onClose={onClose} />;
}

/**
 * Simple windowing for long lists (no dependency): with more than
 * `threshold` items only the rows near the viewport render, with spacers
 * above and below. Works for a scrolling container or the page itself.
 */
export function useWindowed<T>(
  items: T[],
  rowHeight: number,
  container: RefObject<HTMLElement | null>,
  threshold = 200,
) {
  const [range, setRange] = useState({ start: 0, end: items.length });
  const active = items.length > threshold;
  useEffect(() => {
    if (!active) {
      setRange({ start: 0, end: items.length });
      return;
    }
    const scroller = container.current;
    const compute = () => {
      const el = container.current;
      if (!el) return;
      const scrollsItself = el.scrollHeight > el.clientHeight + 1 && getComputedStyle(el).overflowY !== "visible";
      const top = scrollsItself ? el.scrollTop : Math.max(0, -el.getBoundingClientRect().top);
      const height = scrollsItself ? el.clientHeight : window.innerHeight;
      const start = Math.max(0, Math.floor(top / rowHeight) - 10);
      const end = Math.min(items.length, Math.ceil((top + height) / rowHeight) + 10);
      setRange((r) => (r.start === start && r.end === end ? r : { start, end }));
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    scroller?.addEventListener("scroll", compute, { passive: true });
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
      scroller?.removeEventListener("scroll", compute);
    };
  }, [active, items.length, rowHeight, container]);
  const start = active ? Math.min(range.start, items.length) : 0,
    end = active ? Math.min(range.end, items.length) : items.length;
  return {
    windowed: active,
    start,
    visible: items.slice(start, end),
    topHeight: start * rowHeight,
    bottomHeight: Math.max(0, items.length - end) * rowHeight,
  };
}
